/* eslint-disable react-hooks/exhaustive-deps */
import { Close, FilterList } from "@mui/icons-material";
import {
	Checkbox,
	Chip,
	FormControl,
	Grid,
	Grow,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	styled,
} from "@mui/material";
import { api } from "api";
import { GetActivitiesParams } from "api/api";
import { Activity, ActivityType, AllActivityTypes, Realm } from "api/types";
import { InfoSnackBar } from "components/InfoSnackBar";
import { RealmTagList } from "components/RealmTag";
import { StyledButton } from "components/StyledButton";
import { ERR_SEARCH } from "messages";
import { useCallback, useEffect, useState } from "react";

const PREFIX = "ActivitySearchBar";

const classes = {
	searchContainer: `${PREFIX}-searchContainer`,
	button: `${PREFIX}-button`,
	container: `${PREFIX}-container`,
	inputLabel: `${PREFIX}-inputLabel`,
};

const Root = styled("form")(({ theme }) => ({
	[`& .${classes.searchContainer}`]: {
		borderRadius: 4,
		backgroundColor: theme.palette.background.paper,
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		width: "inherit",
	},

	[`& .${classes.button}`]: {
		height: "100%",
	},

	[`& .${classes.container}`]: {
		paddingTop: theme.spacing(2),
	},

	[`& .${classes.inputLabel}`]: {
		"&": {
			backgroundColor: theme.palette.background.paper,
		},
	},
}));

export interface ActivitySearchBarProps {
	handler: (newlyFetchedActivities: Activity[], newQuery: boolean) => void;
	page: number;
	setPage: (page: number) => void;
}

export const ActivitySearchBar = (
	props: ActivitySearchBarProps,
): JSX.Element => {
	const { handler, page, setPage } = props;
	const [expanded, setExpanded] = useState(false);
	const [name, setName] = useState("");
	const [activityType, setActivityType] = useState<ActivityType[]>([]);
	const [realm, setRealm] = useState<Realm[]>([]);
	const [err, setErr] = useState(false);

	const searchActivity = useCallback(
		(newQuery: boolean) => {
			const searchParams: GetActivitiesParams = {
				name,
				activityType,
				realm,
				page: newQuery ? 0 : page,
				count: 10,
			};
			api
				.getActivities({ ...searchParams })
				.then((res) => {
					if (res.status !== 200) {
						setErr(true);
						return;
					}
					if (res.data !== null) {
						if (res.data.length === 0) {
							// if (res.data === null || res.data.length === 0) {
							// no further results to fetch
							setPage(-1);
							// err && setErr(false);
							// return;
						}
						err && setErr(false);
						handler(res.data, newQuery);
					}
				})
				.catch(() => {
					setErr(true);
				});
		},
		[activityType, handler, name, page, realm],
	);

	useEffect(() => {
		// only initiates search call if the page number change is > 0
		// as this is when the user has requested to see more results, the
		// search query is not a new query
		if (page > 0) {
			searchActivity(false);
		}
	}, [page]);
	const resetPage = (): void => {
		setPage(0);
	};

	const handleSubmit = (event: React.FormEvent): void => {
		event.preventDefault();
		// reset page index when a new search is performed
		searchActivity(true);
	};

	const toggleExpansion = (): void => {
		expanded ? setExpanded(false) : setExpanded(true);
	};

	const handleChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
		setName(event.target.value as string);
		resetPage();
	};

	const handleChangeMultiple = (
		event: SelectChangeEvent<ActivityType[]>,
	): void => {
		setActivityType(
			typeof event.target.value === "string"
				? [event.target.value as ActivityType]
				: event.target.value,
		);
		resetPage();
	};

	return (
		<Root onSubmit={handleSubmit}>
			<Grid
				container
				justifyContent="flex-start"
				className={classes.searchContainer}
			>
				<Grid container justifyContent="space-between">
					<Typography variant="h4">Search</Typography>
					{expanded ? (
						<StyledButton variant="contained" onClick={toggleExpansion}>
							<Close /> Close
						</StyledButton>
					) : (
						<StyledButton
							variant="contained"
							color="primary"
							onClick={toggleExpansion}
						>
							<FilterList /> Filter
						</StyledButton>
					)}
				</Grid>
				<Grid container spacing={3} className={classes.container}>
					<Grid item xs={6}>
						<TextField
							label="Search for an activity"
							variant="outlined"
							value={name}
							onChange={handleChange}
							fullWidth
							data-testid="activity-search-name"
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControl variant="outlined" fullWidth>
							<InputLabel className={classes.inputLabel} id="select-label">
								Activity Type
							</InputLabel>
							<Select
								multiple
								labelId="select-label"
								fullWidth
								value={activityType}
								onChange={handleChangeMultiple}
								renderValue={(selected) => (
									<div>
										{(selected as string[]).map((value) => (
											<Chip key={value} label={value} />
										))}
									</div>
								)}
								MenuProps={{
									autoFocus: true,
								}}
							>
								{AllActivityTypes.map((activity) => (
									<MenuItem key={activity} value={activity}>
										<Checkbox checked={activityType.indexOf(activity) > -1} />
										{activity}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={2}>
						<StyledButton
							color="primary"
							variant="contained"
							fullWidth
							className={classes.button}
							type="submit"
						>
							Search
						</StyledButton>
					</Grid>
				</Grid>
				{expanded && (
					<Grow in={expanded} timeout={1000}>
						<Grid container alignItems="flex-start">
							<Typography variant="h6" align="left">
								Realm
							</Typography>
							<Grid container>
								<RealmTagList selected={realm} handler={setRealm} />
							</Grid>
						</Grid>
					</Grow>
				)}
			</Grid>
			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={ERR_SEARCH}
				severity="error"
			/>
		</Root>
	);
};
